import { useParams } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { showToast } from "@/components/shared/Toast";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { useRuleBreakDialog } from "@/components/shared/RuleBreakDialog";
import { AlertTriangle, Zap, Timer } from "lucide-react";
import { useState, useEffect } from "react";

interface Rule {
  _key: string;
  name: string;
  icon: string;
  description: string;
  streak_factor: number;
  punishment_points: number;
  max_points: number | null;
  streak_days: number;
  expires_at: string | null;
}

function formatTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "expired";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

export function ClientRules() {
  const { jwt } = useParams();
  const { getHeaders, user } = useAuth();
  const { decryptList, encryptValue, hasKey } = useEncryption(user?._key);
  const { data: rawData, loading, error, refetch } = useApi<{ rules: Rule[]; current_points: number }>(
    `/api/client/${jwt}/rules`,
  );
  const [breaking, setBreaking] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { openBreakDialog, dialog: breakDialog } = useRuleBreakDialog();
  const [rules, setRules] = useState<Rule[] | null>(null);
  const currentPoints = rawData?.current_points ?? 0;

  // Decrypt rule names/descriptions
  useEffect(() => {
    if (!rawData?.rules) return;
    decryptList(rawData.rules as unknown as Record<string, unknown>[], ["name", "description"])
      .then((d) => setRules(d as unknown as Rule[]));
  }, [rawData?.rules, decryptList]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  const handleBreak = async (ruleKey: string, ruleName: string) => {
    const note = await openBreakDialog(ruleName);
    if (!note) return;

    const ok = await confirm({
      title: "Report rule break?",
      description: `This will deduct points. Note: "${note}"`,
      confirmLabel: "Report Break",
      destructive: true,
    });
    if (!ok) return;

    setBreaking(ruleKey);
    try {
      // Encrypt the note before sending
      const encrypted_note = await encryptValue(note);
      const res = await fetch(`/api/client/${jwt}/rules/${ruleKey}/break`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ encrypted_note }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Rule break reported");
      refetch();
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed to report break");
    } finally {
      setBreaking(null);
    }
  };

  if (!rules || rules.length === 0) {
    return <EmptyState icon="📏" title="No rules set" description="Your admin hasn't assigned any rules yet." />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <h1 className="text-heading font-bold">Your Rules</h1>
      <div className="space-y-2">
        {[...rules]
          .sort((a, b) => {
            const aPaused = a.max_points != null && currentPoints >= a.max_points;
            const bPaused = b.max_points != null && currentPoints >= b.max_points;
            // Active first, paused at bottom
            if (aPaused !== bPaused) return aPaused ? 1 : -1;
            // Paused: sort by max_points (filter) descending
            if (aPaused && bPaused) return (b.max_points ?? 0) - (a.max_points ?? 0);
            // Active: sort by punishment_points descending
            return b.punishment_points - a.punishment_points;
          })
          .map((rule) => {
          const pointsPaused = rule.max_points != null && currentPoints >= rule.max_points;
          return (
          <Card
            key={rule._key}
            className={`border-app-border border-l-4 ${pointsPaused ? "border-l-app-warning opacity-60" : "border-l-app-info"} hover:shadow-md hover:bg-app-surface-raised/50 transition-all duration-200 overflow-hidden py-0 gap-0`}
          >
            <CardContent className="p-2 md:p-2.5">
              <div className="flex items-center gap-2.5">
                {/* Emoji in colored circle */}
                <div className={`w-8 h-8 rounded-lg ${pointsPaused ? "bg-app-warning/10" : "bg-app-info/10"} flex items-center justify-center text-lg shrink-0`}>
                  {rule.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-body font-semibold">{rule.name}</h3>
                    {pointsPaused ? (
                      <span className="text-xs bg-app-warning/10 text-app-warning px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Paused above {rule.max_points} pts
                      </span>
                    ) : rule.streak_days > 0 ? (
                      <span className="text-xs bg-app-success/10 text-app-success px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
                        🔥 {rule.streak_days}d streak
                      </span>
                    ) : null}
                  </div>
                  {rule.description && (
                    <MarkdownText text={rule.description} className="text-info text-app-text-muted mt-0.5" />
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    <span className="text-xs bg-app-danger/10 text-app-danger px-2 py-0.5 rounded-full font-semibold">
                      -{rule.punishment_points} per break
                    </span>
                    <span className="text-xs bg-app-surface-raised px-2 py-0.5 rounded-full font-medium text-app-text-muted">
                      ×{rule.streak_factor} streak multiplier
                    </span>
                    {!pointsPaused && rule.streak_days > 0 && (
                      <span className="text-xs bg-app-success/10 text-app-success px-2 py-0.5 rounded-full font-semibold">
                        +{Math.floor(rule.streak_days * rule.streak_factor)}/day
                      </span>
                    )}
                    {rule.expires_at && new Date(rule.expires_at).getTime() > Date.now() && (
                      <span className="text-xs bg-app-info/10 text-app-info px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
                        <Timer className="w-3 h-3" /> {formatTimeRemaining(rule.expires_at)}
                      </span>
                    )}
                  </div>
                </div>
                {pointsPaused ? (
                  <div className="shrink-0 rounded-lg w-8 h-8 bg-app-warning/10 flex items-center justify-center" title={`Paused — points at or above ${rule.max_points}`}>
                    <Zap className="w-4 h-4 text-app-warning" />
                  </div>
                ) : (
                <Button
                  variant="outline"
                  size="icon"
                  className="border-app-danger text-app-danger hover:bg-app-danger/10 shrink-0 rounded-lg w-8 h-8 transition-all duration-200 hover:shadow-sm hover:scale-105 active:scale-95"
                  onClick={() => handleBreak(rule._key, rule.name)}
                  disabled={breaking === rule._key}
                >
                  <AlertTriangle className="w-5 h-5" />
                </Button>
                )}
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
      {confirmDialog}
      {breakDialog}
    </div>
  );
}
